// Single source of truth for "what URL is this app served from".
// Always uses the current window origin so the URL matches whatever domain
// the user is actively viewing (custom domain, preview, localhost, etc.).

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

function getEnvValue(key: string): string | undefined {
  return typeof import.meta !== "undefined"
    ? (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env?.[key]
    : undefined;
}

/**
 * Returns the public base URL for this deploy, derived from the current
 * browser origin. Returns "" in non-browser contexts (SSR/build); callers
 * should handle that case.
 */
export function getBaseUrl(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return stripTrailingSlash(window.location.origin);
  }
  return "";
}

/**
 * Returns the canonical public site URL (e.g. https://podgo.io) for use in
 * shareable links shown to users. Resolution order:
 *   1. VITE_PUBLIC_SITE_URL (set in .env.production)
 *   2. window.location.origin (preview / localhost)
 */
export function getPublicSiteUrl(): string {
  const env = getEnvValue("VITE_PUBLIC_SITE_URL");
  if (env) return stripTrailingSlash(env);
  return getBaseUrl();
}

export function getRssFeedUrl(slug: string): string {
  const encodedSlug = encodeURIComponent(slug);
  const base = getBaseUrl();
  if (base) return `${base}/rss-feed?slug=${encodedSlug}`;

  // Last-resort fallback when no origin is available (e.g. SSR).
  const backendUrl = getEnvValue("VITE_STANDALONE_SUPABASE_URL") || getEnvValue("VITE_SUPABASE_URL");
  if (backendUrl) {
    return `${stripTrailingSlash(backendUrl)}/functions/v1/rss-feed?slug=${encodedSlug}`;
  }
  return `/rss-feed?slug=${encodedSlug}`;
}
